import { useFormContext, Controller, useWatch } from "react-hook-form";
import Input from "components/Input";
import Select from "components/Select";
import Textarea from "components/Textarea";
import { CARRIER_FR_CONTRACT_TYPE, FR_CONTRACT_TYPE, FR_STATUS } from "constants/haulpay-broker";
import { useFundingRequestForm } from "./FundingRequestFormContext";
import { ICompany } from "models/HaulPay";
import { useUserFeature } from "hooks/useUserFeature";
import { useSelector } from "react-redux";
import { selectFactoringProfile } from "store/user/selector";
import { IOption } from "models/Form";
import { useEffect, useMemo } from "react";
import DebtorSearch from "components/DebtorSearchInput";
import DollarInput from "components/DollarInput";
import { selectAccount } from "store/auth/selector";
import Icon from "@mdi/react";
import { mdiPencilOutline } from "@mdi/js";
import { useToggle } from "usehooks-ts";
import Modal from "components/Modal";
import FRLineItems from "../../../modules/Broker/FRLineItems";
import { DECLINED_DEBTOR_FACTORING_NOTICE, isDeclinedDebtor } from "helpers/debtorCredit";
import { formatNumber } from "helpers/formatNumber";
import Banner from "components/Banner";
import Button from "components/Button";
import clsx from "clsx";
import { IEditFundingRequest } from "../../EditFundingRequest/StepOne";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import PhoneNumber from "components/PhoneNumber";
import ControlledTooltip from "./ControlledTooltip";
import {
  canBrokerEditApprovedSelfFinanceShipperAmount,
  canBrokerEditSelfFinanceInvoiceReferenceFields,
  isCarrierFRFieldsLocked
} from "./permissions";

export default function FundingRequestInfo() {
  const {
    isGlobalDisabled,
    loading,
    isAssignedLoadsPage,
    isTypeDisabled,
    isOtherFieldsDisabled,
    typeDisabledTooltip,
    otherFieldsDisabledTooltip,
    selectedFR
  } = useFundingRequestForm();
  const isCarrierFrEditable = selectedFR?.status === "Incomplete";

  const {
    control,
    setValue,
    formState: { errors },
    clearErrors
  } = useFormContext<Partial<IEditFundingRequest>>();
  const { isCarrier, isBroker, isAllowLineItems, isAutoGenerateLoadNumbersEnabled } =
    useUserFeature();
  // HP-9253: once a carrier's request reaches Pending or Fuel Advance the load number and status
  // note are no longer theirs to change. Both stayed editable because those statuses are in
  // CARRIER_EDITABLE_STATUSES for the rest of the form, and the load number silently failed to
  // save while still reporting success.
  const isCarrierLockedField = isCarrierFRFieldsLocked({ isCarrier, status: selectedFR?.status });
  const { userPermission } = useSelector(selectAccount);
  const contractType = useWatch({ control: control, name: "fundingRequestType" });
  const frStatus = useWatch({ control: control, name: "fundingRequestStatus" });
  const fundingRequestTypeValue = useWatch({
    control: control,
    name: "fundingRequestType.value"
  });
  const debtor = useWatch({ control: control, name: "debtor" });
  const isApprovedNonFactoredPayCarrier =
    (frStatus?.value === "Remote Approved" || frStatus?.value === "Approved") &&
    contractType?.value === "NON_FACTORED_STANDARD" &&
    !selectedFR?.purchases?.length;
  const allowShipperAmountEdit = canBrokerEditApprovedSelfFinanceShipperAmount({
    isBroker,
    status: selectedFR?.status,
    contractType: selectedFR?.contractType,
    receivableStatus: selectedFR?.receivableStatus
  });
  const allowInvoiceReferenceEdit = canBrokerEditSelfFinanceInvoiceReferenceFields({
    isBroker,
    status: selectedFR?.status,
    contractType: selectedFR?.contractType,
    receivableStatus: selectedFR?.receivableStatus
  });
  const factoringProfile = useSelector(selectFactoringProfile);
  const useNewNFRequest = factoringProfile?.use_new_non_factored_funding_requests;
  const enableHoldReviewFundingRequestStatus =
    factoringProfile?.enable_hold_review_funding_request_status;
  const useNewCarrierFundingRequest = factoringProfile?.use_new_carrier_funding_requests ?? false;
  const [isEditInvoiceAmount, toggleEditInvoiceAmountModal] = useToggle();
  const shipperInvoiceAmount = useWatch({ control: control, name: "shipperInvoiceAmount" });
  const isCarrierTypeLockedByStatus = isCarrier && selectedFR?.status !== "Incomplete";
  const carrierInvoiceDebtorLocked = isCarrier && selectedFR?.status !== "Incomplete";
  const carrierInvoiceDebtorLockedTooltip =
    "You can only edit invoice amount or debtor when the load is Incomplete.";
  const isFundingRequestTypeDisabled =
    isGlobalDisabled || isCarrierTypeLockedByStatus || isTypeDisabled;
  const fundingRequestTypeTooltip = isCarrierTypeLockedByStatus
    ? "Carrier funding request type can only be changed while the request is Incomplete."
    : typeDisabledTooltip;
  const isDeclinedDebtorSelected = isDeclinedDebtor(debtor);
  const isCarrierContractTypeEditable =
    isCarrier &&
    useNewCarrierFundingRequest &&
    !isAssignedLoadsPage &&
    !isFundingRequestTypeDisabled;
  const carrierContractTypeOptions: IOption[] = useMemo(
    () =>
      CARRIER_FR_CONTRACT_TYPE.map((option) =>
        isDeclinedDebtorSelected && option.value === "STD_CARRIER"
          ? { ...option, isDisabled: true }
          : option
      ),
    [isDeclinedDebtorSelected]
  );

  // Disabling the Factored option does not clear an already selected one, so an
  // Incomplete request that switches to a declined debtor is moved off Factored.
  useEffect(() => {
    if (
      isCarrierContractTypeEditable &&
      isDeclinedDebtorSelected &&
      fundingRequestTypeValue === "STD_CARRIER"
    ) {
      setValue(
        "fundingRequestType",
        CARRIER_FR_CONTRACT_TYPE.find((option) => option.value === "NON_FACTORED_BILL_OUT"),
        { shouldDirty: true }
      );
    }
  }, [isCarrierContractTypeEditable, isDeclinedDebtorSelected, fundingRequestTypeValue, setValue]);

  const fundingRequestStatusOptions: IOption[] = useMemo(() => {
    return FR_STATUS.filter((option) => {
      if (!enableHoldReviewFundingRequestStatus && option.id === 1) return false;
      if (option.id === 4) return false;
      return true;
    }).map((option) => {
      if (
        (useNewNFRequest && (option.id === 6 || option.id === 7)) ||
        option.id === 8 ||
        option.id === 9
      ) {
        return { ...option, isDisabled: true };
      }
      return option;
    });
  }, [useNewNFRequest, enableHoldReviewFundingRequestStatus]);

  const onChangeLineItemAmount = (amount: number) => {
    setValue("shipperInvoiceAmount", amount ?? 0);
  };
  const handleAmountUpdate = () => {
    setValue("shipperInvoiceAmount", shipperInvoiceAmount ?? ("" as any));
    toggleEditInvoiceAmountModal();
  };

  // Custom class for reduced input height
  const shortInputClass =
    "placeholder:text-slate-400 text-slate-700 !text-sm border border-slate-200 !rounded !px-4 !py-2 !h-10 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 shadow-sm focus:shadow";

  if (loading) {
    return (
      <div className="animate-pulse flex flex-col gap-4">
        <div className="h-10 bg-foreground rounded-md"></div>
        <div className="h-10 bg-foreground rounded-md"></div>
        <div className="h-10 bg-foreground rounded-md"></div>
      </div>
    );
  }

  // Carrier View
  if (isCarrier) {
    return (
      <div className="p-3 space-y-6 rounded-lg">
        <div className="grid grid-cols-1 desktop:grid-cols-3 gap-6">
          {/* Total Invoice Amount */}
          <ControlledTooltip
            id="carrier-total-invoice-amount"
            message={
              carrierInvoiceDebtorLocked
                ? carrierInvoiceDebtorLockedTooltip
                : otherFieldsDisabledTooltip
            }
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled && !carrierInvoiceDebtorLocked}>
            <div>
              <h6 className="font-normal mb-2">Total Invoice Amount</h6>
              <Controller
                name="shipperInvoiceAmount"
                control={control}
                render={({ field }) => (
                  <DollarInput
                    label=""
                    {...field}
                    disabled={
                      isGlobalDisabled ||
                      isAssignedLoadsPage ||
                      isOtherFieldsDisabled ||
                      carrierInvoiceDebtorLocked
                    }
                    className="!rounded-l-none"
                    inputClass={shortInputClass}
                    prefixUnitClass="!px-3 !py-[0.25rem] flex items-center justify-center font-thin !rounded !rounded-r-none !h-10"
                    error={errors?.shipperInvoiceAmount}
                  />
                )}
              />
            </div>
          </ControlledTooltip>

          <ControlledTooltip
            id="carrier-debtor-advance"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled && isCarrierFrEditable}>
            <div>
              <h6 className="font-normal mb-2">Debtor Advance</h6>
              <Controller
                name="debtorAdvanceAmount"
                control={control}
                render={({ field }) => (
                  <DollarInput
                    label=""
                    {...field}
                    disabled={isGlobalDisabled || isOtherFieldsDisabled || !isCarrierFrEditable}
                    className="!rounded-l-none"
                    inputClass={shortInputClass}
                    prefixUnitClass="!px-3 !py-[0.25rem] flex items-center justify-center font-thin !rounded !rounded-r-none !h-10"
                    error={errors?.debtorAdvanceAmount as any}
                  />
                )}
              />
            </div>
          </ControlledTooltip>

          {/* Your Invoice Number */}
          <ControlledTooltip
            id="carrier-invoice-number"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div>
              <h6 className="font-normal mb-2">Invoice #</h6>
              <Controller
                name="invoiceNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={
                      isAutoGenerateLoadNumbersEnabled ||
                      isCarrier ||
                      isGlobalDisabled ||
                      isAssignedLoadsPage ||
                      isOtherFieldsDisabled
                    }
                    inputClass={shortInputClass}
                    error={errors?.invoiceNumber}
                  />
                )}
              />
            </div>
          </ControlledTooltip>
        </div>

        {/* Bill To */}
        <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
          <ControlledTooltip
            id="carrier-bill-to"
            message={
              carrierInvoiceDebtorLocked
                ? carrierInvoiceDebtorLockedTooltip
                : otherFieldsDisabledTooltip
            }
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled && !carrierInvoiceDebtorLocked}>
            <div>
              <h6 className="font-normal mb-2">Bill To</h6>
              <DebtorSearch
                name="debtor"
                value={debtor?.name}
                onSelect={(company: ICompany) => {
                  setValue("debtor.phoneNumber", company.phone_number);
                  setValue("debtor.email", company.email);
                  setValue("debtor", company);
                  clearErrors("debtor");
                }}
                error={errors?.debtor as any}
                disabled={
                  isGlobalDisabled ||
                  isAssignedLoadsPage ||
                  isOtherFieldsDisabled ||
                  carrierInvoiceDebtorLocked
                }
                isConnectedDebtor
                inputClass={shortInputClass}
              />
            </div>
          </ControlledTooltip>

          {useNewCarrierFundingRequest && !isAssignedLoadsPage && (
            <ControlledTooltip
              id="carrier-funding-request-type"
              message={fundingRequestTypeTooltip}
              position="down"
              tooltipContainerClassName="relative"
              wrapperClass="max-w-xs !whitespace-normal"
              disabled={!isFundingRequestTypeDisabled}>
              <div>
                <h6 className="font-normal mb-2">Funding Request Type</h6>
                <Controller
                  name="fundingRequestType"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      disabled={isFundingRequestTypeDisabled}
                      options={carrierContractTypeOptions}
                      btnClass="!py-1"
                      inputClass={shortInputClass}
                      error={errors?.fundingRequestType as any}
                    />
                  )}
                />
              </div>
            </ControlledTooltip>
          )}
        </div>
        {isDeclinedDebtorSelected && useNewCarrierFundingRequest && !isAssignedLoadsPage && (
          <Banner variant="warning" message={DECLINED_DEBTOR_FACTORING_NOTICE} />
        )}
        <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
          <div>
            <h6 className="font-normal mb-2">Bill To Company Address</h6>
            <Controller
              name="billToCompanyAddress"
              control={control}
              render={({ field: { value, onChange } }) => (
                <GoogleAutoComplete
                  value={value}
                  onChange={onChange}
                  inputClass={shortInputClass}
                  error={errors.billToCompanyAddress as any}
                  disabled
                />
              )}
            />
          </div>
          <div>
            <h6 className="font-normal mb-2">Bill To Company Phone</h6>
            <Controller
              name="billToCompanyPhone"
              control={control}
              render={({ field: { value, onChange } }) => (
                <PhoneNumber
                  value={value}
                  onChange={onChange}
                  error={errors.billToCompanyPhone}
                  inputClass={shortInputClass}
                  wrapperClass="flex-1"
                  disabled
                />
              )}
            />
          </div>
        </div>
        {/* Load # */}
        <ControlledTooltip
          id="carrier-load-number"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">Load #</h6>
            <Controller
              name="userLoadNumber"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  disabled={
                    isGlobalDisabled ||
                    isAssignedLoadsPage ||
                    isOtherFieldsDisabled ||
                    isCarrierLockedField
                  }
                  inputClass={shortInputClass}
                  error={errors?.userLoadNumber}
                />
              )}
            />
          </div>
        </ControlledTooltip>

        {/* HP-9215: the status note is the broker's. On an assigned load the carrier is viewing a
            broker funding request, so it is withheld; on their own request it stays editable. */}
        {!isAssignedLoadsPage && (
          <ControlledTooltip
            id="carrier-status-note"
            message={otherFieldsDisabledTooltip}
            position="down"
            tooltipContainerClassName="relative"
            wrapperClass="max-w-xs !whitespace-normal"
            disabled={!isOtherFieldsDisabled}>
            <div>
              <h6 className="font-normal mb-2">Status Note:</h6>
              <Controller
                name="statusNote"
                control={control}
                render={({ field }) => (
                  <Textarea
                    inputClass={clsx(
                      "mobile:min-h-[200px]",
                      "disabled:cursor-not-allowed",
                      "disabled:bg-inputDark"
                    )}
                    rows={2}
                    placeholder="Enter status notes and additional information..."
                    {...field}
                    disabled={isGlobalDisabled || isOtherFieldsDisabled || isCarrierLockedField}
                  />
                )}
              />
            </div>
          </ControlledTooltip>
        )}

        {/* Payment Method */}
        <div>
          <h6 className="font-normal mb-2">Payment Method</h6>
          <Controller
            name="paymentProfileMethod"
            control={control}
            render={({ field }) => (
              <Input label="" {...field} disabled inputClass={shortInputClass} />
            )}
            disabled
          />
        </div>
      </div>
    );
  }

  // Broker View
  return (
    <div className="p-3 space-y-6 rounded-lg">
      <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
        {/* Invoice # */}
        <ControlledTooltip
          id="broker-invoice-number"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">Invoice #</h6>
            <Controller
              name="invoiceNumber"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  disabled={isGlobalDisabled || isOtherFieldsDisabled}
                  inputClass={shortInputClass}
                  error={errors?.invoiceNumber}
                />
              )}
            />
          </div>
        </ControlledTooltip>

        <ControlledTooltip
          id="broker-funding-request-type"
          message={typeDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isTypeDisabled}>
          <div>
            <h6 className="font-normal mb-2">Funding Request Type</h6>
            <Controller
              name="fundingRequestType"
              control={control}
              render={({ field }) => (
                <Select
                  label=""
                  options={
                    isApprovedNonFactoredPayCarrier
                      ? FR_CONTRACT_TYPE.filter(
                          (request) =>
                            (request.value === "NON_FACTORED_STANDARD" ||
                              request.value === "SELF_FINANCE_WITH_INVOICE" ||
                              request.value === "SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM") &&
                            !request.hidden &&
                            (userPermission?.available_contract_types?.[request.value] ?? false)
                        )
                      : FR_CONTRACT_TYPE.filter(
                          (request) =>
                            !request.hidden &&
                            (userPermission?.available_contract_types?.[request.value] ?? false)
                        )
                  }
                  value={field.value}
                  onChange={field.onChange}
                  disabled={
                    (isGlobalDisabled && !isApprovedNonFactoredPayCarrier) || isTypeDisabled
                  }
                  btnClass="!py-1"
                  inputClass={shortInputClass}
                  error={errors?.fundingRequestType as any}
                />
              )}
            />
          </div>
        </ControlledTooltip>
      </div>

      <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
        <ControlledTooltip
          id="broker-shipper-debtor"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">Shipper/Debtor</h6>
            <DebtorSearch
              name="debtor"
              value={debtor?.name}
              onSelect={(company: ICompany) => {
                setValue("debtor.phoneNumber", company.phone_number);
                setValue("debtor.email", company.email);
                setValue("debtor", company);
                clearErrors("debtor");
              }}
              error={errors?.debtor as any}
              disabled={
                fundingRequestTypeValue === FR_CONTRACT_TYPE[4].value ||
                isGlobalDisabled ||
                isOtherFieldsDisabled
              }
              isConnectedDebtor
              inputClass={clsx(
                shortInputClass,
                errors?.debtor && "!border-error !ring-1 !ring-error"
              )}
            />
            <p className="text-sm italic text-info mt-1">Carriers will not see this</p>
          </div>
        </ControlledTooltip>
        <ControlledTooltip
          id="broker-shipper-invoice-amount"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <div className="flex items-center justify-between gap-2">
              <h6 className="font-normal mb-2">Shipper Invoice Amnt. </h6>
            </div>
            <Controller
              name="shipperInvoiceAmount"
              control={control}
              render={({ field }) => (
                <DollarInput
                  label=""
                  {...field}
                  disabled={
                    isOtherFieldsDisabled || (allowShipperAmountEdit ? false : isGlobalDisabled)
                  }
                  className={`!rounded-l-none`}
                  inputClass={shortInputClass}
                  prefixUnitClass="!px-3 !py-[0.25rem] flex items-center justify-center font-thin !rounded !rounded-r-none !h-10"
                  error={errors?.shipperInvoiceAmount}
                />
              )}
            />
            {isAllowLineItems ? (
              <button
                type="button"
                onClick={toggleEditInvoiceAmountModal}
                disabled={isOtherFieldsDisabled}
                className="flex items-center justify-end text-info text-sm pt-1 disabled:opacity-50">
                <Icon path={mdiPencilOutline} className="flex-none size-3.5" />
                <span>(Edit line items)</span>
              </button>
            ) : null}
          </div>
        </ControlledTooltip>
      </div>
      <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
        <ControlledTooltip
          id="broker-load-number"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">Load #</h6>
            <Controller
              name="userLoadNumber"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  disabled={isGlobalDisabled || isOtherFieldsDisabled || isCarrierLockedField}
                  inputClass={shortInputClass}
                  error={errors?.userLoadNumber}
                />
              )}
            />
          </div>
        </ControlledTooltip>
        {/* Funding Request Status */}
        <ControlledTooltip
          id="broker-funding-request-status"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">Funding Request Status</h6>
            <Controller
              name="fundingRequestStatus"
              control={control}
              render={({ field: { value = fundingRequestStatusOptions[0], onChange } }) => (
                <Select
                  label=""
                  options={fundingRequestStatusOptions}
                  value={value}
                  onChange={(status) => {
                    if ([FR_STATUS[6].value, FR_STATUS[7].value].includes(status.value)) {
                      const fundingRequestType = FR_CONTRACT_TYPE.find(
                        ({ value }) => status.value === value
                      );
                      fundingRequestType && setValue("fundingRequestType", fundingRequestType);
                    }
                    onChange(status);
                  }}
                  disabled={
                    isGlobalDisabled ||
                    isOtherFieldsDisabled ||
                    (contractType &&
                      ["NON_FACTORED_STANDARD", "NON_FACTORED_BILL_OUT"].includes(
                        contractType?.value
                      ) &&
                      !useNewNFRequest) ||
                    (frStatus &&
                      [FR_STATUS[6].value, FR_STATUS[7].value, FR_STATUS[9].value].includes(
                        frStatus?.value
                      ))
                  }
                  btnClass="!py-1"
                  inputClass={shortInputClass}
                />
              )}
            />
          </div>
        </ControlledTooltip>
      </div>
      <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
        <ControlledTooltip
          id="broker-po-number"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">PO#</h6>
            <Controller
              name="PONumber"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  disabled={
                    isOtherFieldsDisabled || (allowInvoiceReferenceEdit ? false : isGlobalDisabled)
                  }
                  inputClass={shortInputClass}
                  error={errors?.PONumber}
                />
              )}
            />
            <p className="text-sm italic text-info mt-1">Visible to Shipper Only</p>
          </div>
        </ControlledTooltip>
        <ControlledTooltip
          id="broker-client-invoice-number"
          message={otherFieldsDisabledTooltip}
          position="down"
          tooltipContainerClassName="relative"
          wrapperClass="max-w-xs !whitespace-normal"
          disabled={!isOtherFieldsDisabled}>
          <div>
            <h6 className="font-normal mb-2">Client Invoice #</h6>
            <Controller
              name="clientInvoiceNumber"
              control={control}
              render={({ field }) => (
                <Input
                  label=""
                  {...field}
                  disabled={
                    isOtherFieldsDisabled || (allowInvoiceReferenceEdit ? false : isGlobalDisabled)
                  }
                  inputClass={shortInputClass}
                  error={errors?.clientInvoiceNumber}
                />
              )}
            />
            <p className="text-sm italic text-info mt-1">Visible to Shipper and Carrier</p>
          </div>
        </ControlledTooltip>
      </div>
      <ControlledTooltip
        id="broker-debtor-invoice-details"
        message={otherFieldsDisabledTooltip}
        position="down"
        tooltipContainerClassName="relative"
        wrapperClass="max-w-xs !whitespace-normal"
        disabled={!isOtherFieldsDisabled}>
        <div className="pt-4">
          <h6 className="font-normal mb-2">Shipper/Debtor Invoice Details</h6>
          <Controller
            name="debtorInvoiceDetails"
            control={control}
            render={({ field }) => (
              <Textarea
                inputClass={clsx(
                  "mobile:min-h-[200px]",
                  "disabled:cursor-not-allowed",
                  "disabled:bg-inputDark"
                )}
                rows={2}
                {...field}
                disabled={isGlobalDisabled || isOtherFieldsDisabled}
              />
            )}
          />
          <p className="text-sm italic text-info mt-1">Visible to Shipper Only</p>
        </div>
      </ControlledTooltip>
      <ControlledTooltip
        id="broker-internal-notes"
        message={otherFieldsDisabledTooltip}
        position="down"
        tooltipContainerClassName="relative"
        wrapperClass="max-w-xs !whitespace-normal"
        disabled={!isOtherFieldsDisabled}>
        <div className="pt-4">
          <h6 className="font-normal mb-2">Internal Funding Request Notes</h6>
          <Controller
            name="userNotes"
            control={control}
            render={({ field }) => (
              <Textarea
                inputClass={clsx(
                  "mobile:min-h-[200px]",
                  "disabled:cursor-not-allowed",
                  "disabled:bg-inputDark"
                )}
                rows={2}
                {...field}
                disabled={isGlobalDisabled || isOtherFieldsDisabled}
              />
            )}
          />
          <p className="text-sm italic text-info mt-1">Visible to HaulPay Operations Team</p>
        </div>
      </ControlledTooltip>
      <ControlledTooltip
        id="broker-status-note"
        message={otherFieldsDisabledTooltip}
        position="down"
        tooltipContainerClassName="relative"
        wrapperClass="max-w-xs !whitespace-normal"
        disabled={!isOtherFieldsDisabled}>
        <div className="pt-4">
          <h6 className="font-normal mb-2">Status Note:</h6>
          <Controller
            name="statusNote"
            control={control}
            render={({ field }) => (
              <Textarea
                inputClass={clsx(
                  "mobile:min-h-[200px]",
                  "disabled:cursor-not-allowed",
                  "disabled:bg-inputDark"
                )}
                rows={2}
                placeholder="Enter status notes and additional information..."
                {...field}
                disabled={isGlobalDisabled || isOtherFieldsDisabled || isCarrierLockedField}
              />
            )}
          />
          <p className="text-sm italic text-info mt-1">For HaulPay Internal Use Only</p>
        </div>
      </ControlledTooltip>
      <Modal
        isOpen={isEditInvoiceAmount}
        closeModal={toggleEditInvoiceAmountModal}
        wrapperClass="text-sm desktop:!min-h-fit desktop:!w-1/3"
        dialogClass="!bg-foreground !p-4 !border !border-card !overflow-y-auto gap-4"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <h3 className="text-xl font-bold text-primary">Funding Request Line Items</h3>
        <div className="flex flex-col gap-3">
          <FRLineItems onChangeLineItemAmount={onChangeLineItemAmount} />
          <div className="flex justify-between">
            <span>Shipper Invoice Amount</span>
            <span>{formatNumber(shipperInvoiceAmount ?? 0, 2)}</span>
          </div>
        </div>
        <Button type="button" onClick={handleAmountUpdate} className="self-end w-20">
          Done
        </Button>
      </Modal>
    </div>
  );
}
